<template>
  <div>
    <h2>Publication Results (Quick view)</h2>

    <div v-if="hasShortAnswers" class="IATreatment">
      <h5>IA Correction Process</h5>
      <p>
        This process will correct the short answer questions using an AI model.
        It may take some time depending on the number of questions and the length of the answers.</p>

      <button @click="launchAICorrection" :disabled="aiProcessing || !hasShortAnswers" class="btn btn-primary mb-3">
        {{ aiProcessing ? "Correction in progress..." : "Launch AI Correction" }}
      </button>
    </div>
    <hr>
    <button style="margin-left:10px; margin-right: 10px;" @click="handleDownloadJSON" class="btn btn-success mb-3">
      Download results (JSON)
    </button>

    <button @click="handleDownloadCSV" class="btn btn-warning mb-3">
      Download results (CSV)
    </button>

    <!-- Contrôles de vue et recherche -->
    <div class="controls-section mb-4">
      <div class="row align-items-center">
        <div class="col-md-4">
          <button 
            @click="toggleView" 
            class="btn"
            :class="showMetricsOnly ? 'btn-outline-primary' : 'btn-primary'"
            :disabled="loading"
          >
            {{ showMetricsOnly ? 'View details' : 'Summary view' }}
            <span v-if="loading" class="spinner-border spinner-border-sm ms-2"></span>
          </button>
        </div>
        <div class="col-md-4" v-if="!showMetricsOnly">
          <div class="input-group">
            <input 
              v-model="searchQuery"
              @keyup.enter="handleSearch"
              type="text" 
              class="form-control" 
              placeholder="Search for a student..."
            >
            <button @click="handleSearch" class="btn btn-outline-secondary" :disabled="loading">
              🔍
            </button>
          </div>
        </div>
        <div class="col-md-4 text-end" v-if="!showMetricsOnly && totalCount > 0">
          <small class="text-muted">
            {{ ((currentPage - 1) * pageSize) + 1 }}-{{ Math.min(currentPage * pageSize, totalCount) }} 
            of {{ totalCount }} results
          </small>
        </div>
      </div>
    </div>

    <!-- Indicateurs synthétiques -->
    <div v-if="(metricsData && metricsData.totalStudents > 0) || results.length > 0" class="results-dashboard">
      <!-- Vue d'ensemble -->
      <div class="row mb-4">
        <div class="col-md-3">
          <div class="metric-card">
            <div class="metric-number">{{ totalStudents }}</div>
            <div class="metric-label">Participating students</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="metric-card">
            <div class="metric-number">{{ totalAttempts }}</div>
            <div class="metric-label">Total attempts</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="metric-card">
            <div class="metric-number">{{ averageScore }}%</div>
            <div class="metric-label">Average score</div>
          </div>
        </div>
        <div class="col-md-3">
          <div class="metric-card">
            <div class="metric-number">{{ completionRate }}%</div>
            <div class="metric-label">Completion rate</div>
          </div>
        </div>
      </div>

      <!-- Score distribution -->
      <div class="row mb-4">
        <div class="col-12">
          <div class="chart-card">
            <h5>Score distribution</h5>
            <div class="score-distribution">
              <div v-for="range in scoreRanges" :key="range.label" class="score-bar-container">
                <div class="score-label">{{ range.label }}</div>
                <div class="score-bar">
                  <div 
                    class="score-fill" 
                    :style="{ width: range.percentage + '%', backgroundColor: range.color }"
                  ></div>
                </div>
                <div class="score-count">{{ range.count }} ({{ range.percentage }}%)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Student summary table -->
      <div class="row" v-if="!showMetricsOnly">
        <div class="col-12">
          <div class="table-card">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5>Summary by student 
                <small class="text-muted">({{ totalCount > 0 ? totalCount : studentSummary.length }} students)</small>
              </h5>
              
              <!-- Sort options -->
              <div class="btn-group btn-group-sm">
                <button 
                  @click="handleSort('submittedAt')"
                  class="btn btn-outline-secondary"
                  :class="{ active: sortBy === 'submittedAt' }"
                >
                  Date {{ sortBy === 'submittedAt' ? (sortOrder === 'desc' ? '↓' : '↑') : '' }}
                </button>
                <button 
                  @click="handleSort('studentLastname')"
                  class="btn btn-outline-secondary"
                  :class="{ active: sortBy === 'studentLastname' }"
                >
                  Name {{ sortBy === 'studentLastname' ? (sortOrder === 'desc' ? '↓' : '↑') : '' }}
                </button>
              </div>
            </div>

            <div class="table-responsive">
              <table class="table table-sm table-hover">
                <thead class="table-light">
                  <tr>
                    <th>Student</th>
                    <th>Attempts</th>
                    <th>Best score</th>
                    <th>Average score</th>
                    <th>Last attempt</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="student in studentSummary" :key="student.studentId">
                    <td>
                      <strong>{{ student.name }}</strong>
                      <br>
                      <small class="text-muted">{{ student.studentId }}</small>
                    </td>
                    <td>
                      <span class="badge bg-info">{{ student.attemptCount }}</span>
                    </td>
                    <td>
                      <span class="score-badge" :class="getScoreClass(student.bestScore)">
                        {{ student.bestScore }}%
                      </span>
                    </td>
                    <td>{{ student.averageScore }}%</td>
                    <td>
                      <small>{{ formatDate(student.lastAttempt) }}</small>
                    </td>
                    <td>
                      <span class="badge" :class="getStatusClass(student.bestScore)">
                        {{ getStatusLabel(student.bestScore) }}
                      </span>
                    </td>
                  </tr>
                  
                  <!-- Loading row -->
                  <tr v-if="loading">
                    <td colspan="6" class="text-center py-3">
                      <div class="spinner-border spinner-border-sm me-2"></div>
                      Loading...
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-3">
              <div>
                <small class="text-muted">
                  Page {{ currentPage }} of {{ totalPages }}
                </small>
              </div>
              
              <nav>
                <ul class="pagination pagination-sm mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 || loading }">
                    <button @click="goToPage(1)" class="page-link">«</button>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === 1 || loading }">
                    <button @click="goToPage(currentPage - 1)" class="page-link">‹</button>
                  </li>
                  
                  <!-- Visible pages -->
                  <template v-for="page in visiblePages" :key="page">
                    <li class="page-item" :class="{ active: page === currentPage }">
                      <button @click="goToPage(page)" class="page-link">{{ page }}</button>
                    </li>
                  </template>
                  
                  <li class="page-item" :class="{ disabled: currentPage === totalPages || loading }">
                    <button @click="goToPage(currentPage + 1)" class="page-link">›</button>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages || loading }">
                    <button @click="goToPage(totalPages)" class="page-link">»</button>
                  </li>
                </ul>
              </nav>

              <!-- Progressive loading -->
              <div>
                <button 
                  v-if="currentPage < totalPages" 
                  @click="loadNextPage"
                  class="btn btn-sm btn-outline-primary"
                  :disabled="loading"
                >
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Load more
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <p v-else-if="!loading && (!metricsData || (metricsData && metricsData.totalStudents === 0))" class="no-results">
      No results available for this publication.
    </p>
    
    <div v-else-if="loading && !metricsData" class="text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import api from "@/services/axios";
import { downloadJSON, downloadCSV } from "@/utils/downloadUtils";

const props = defineProps({
  publication: {
    type: Object,
    required: true,
  },
});

const results = ref([]);
const studentNames = ref({});
const aiProcessing = ref(false);
const sessionData = ref([]);
const studentAttemptCount = ref({});
const testDetails = ref(null);
const hasShortAnswers = ref(false);

// Nouvelles variables pour la pagination
const currentPage = ref(1);
const pageSize = ref(50);
const totalPages = ref(0);
const totalCount = ref(0);
const loading = ref(false);
const searchQuery = ref('');
const sortBy = ref('submittedAt');
const sortOrder = ref('desc');
const metricsData = ref(null);
const showMetricsOnly = ref(true);


const handleDownloadJSON = () => {
  downloadJSON(props.publication, sessionData.value, studentNames.value, studentAttemptCount.value, results.value, results.value);
};

const handleDownloadCSV = () => {
  downloadCSV(props.publication, studentNames.value, studentAttemptCount.value, results.value);
};

// ✅ Fonction pour récupérer les détails du test
const fetchTest = async () => {
  if (!props.publication.testId.value) return;
  try {
    const apiUrl = `/tests/${props.publication.testId.value}`;
    const response = await api.get(apiUrl);
    testDetails.value = response.data;
    hasShortAnswers.value = testDetails.value.questions.some(q => q.type === "shortAnswer");
  } catch (error) {
    console.error("❌ Erreur lors de la récupération du test :", error);
  }
};

// ✅ Fonction pour récupérer uniquement les métriques (rapide)
const fetchMetrics = async () => {
  if (!props.publication._id) return;
  loading.value = true;
  
  console.log("🔄 Récupération des métriques pour publication:", props.publication._id);
  
  try {
    const response = await api.get(`/results/publication/${props.publication._id}?onlyMetrics=true`);
    
    console.log("📡 Réponse API métriques:", response.data);
    
    if (response.data && response.data.metrics) {
      metricsData.value = response.data.metrics;
      console.log("✅ Métriques assignées:", metricsData.value);
      console.log("🔍 Détail métriques:", JSON.stringify(metricsData.value, null, 2));
    } else {
      console.log("⚠️ Pas de métriques dans la réponse, utilisation fallback");
      
      // Fallback vers l'ancienne API si pas de métriques
      const fallbackResponse = await api.get(`/results/publication/${props.publication._id}`);
      
      if (Array.isArray(fallbackResponse.data)) {
        results.value = fallbackResponse.data;
        
        // Calculer les métriques côté client
        const uniqueStudents = new Set(fallbackResponse.data.map(r => 
          typeof r.studentId === 'object' ? r.studentId._id : r.studentId
        )).size;
        
        const scores = fallbackResponse.data.map(result => {
          const totalQuestions = result.responses.length;
          if (totalQuestions === 0) return 0;
          
          const correctAnswers = result.responses.reduce((sum, response) => {
            return sum + (parseFloat(response.score) || 0);
          }, 0);
          
          return (correctAnswers / totalQuestions) * 100;
        });
        
        const averageScore = scores.length > 0 
          ? scores.reduce((sum, score) => sum + score, 0) / scores.length 
          : 0;
        
        metricsData.value = {
          totalStudents: uniqueStudents,
          totalAttempts: fallbackResponse.data.length,
          averageScore: averageScore,
          completionRate: 100,
          scoreDistribution: [] // Sera calculé par computed properties
        };
        
        console.log("✅ Métriques calculées côté client:", metricsData.value);
      } else {
        throw new Error("Format de données inattendu");
      }
    }
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des métriques :", error);
    
    metricsData.value = {
      totalStudents: 0,
      totalAttempts: 0,
      averageScore: 0,
      completionRate: 0,
      scoreDistribution: []
    };
  } finally {
    loading.value = false;
  }
};

// ✅ Fonction pour récupérer les résultats paginés
const fetchResults = async (page = 1, append = false) => {
  if (!props.publication._id) return;
  loading.value = true;
  
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: pageSize.value.toString(),
      sortBy: sortBy.value,
      sortOrder: sortOrder.value
    });

    if (searchQuery.value) {
      params.append('search', searchQuery.value);
    }

    const response = await api.get(`/results/publication/${props.publication._id}?${params}`);

    if (response.data.data && Array.isArray(response.data.data)) {
      if (append) {
        results.value = [...results.value, ...response.data.data];
      } else {
        results.value = response.data.data;
      }
      
      currentPage.value = response.data.pagination.currentPage;
      totalPages.value = response.data.pagination.totalPages;
      totalCount.value = response.data.pagination.totalCount;
      
      await fetchStudentNames();
    }
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des résultats paginés :", error);
    
    // Fallback vers l'ancienne API si on est à la page 1
    if (page === 1 && !append) {
      try {
        console.log("🔄 Fallback vers ancienne API pour les résultats...");
        const fallbackResponse = await api.get(`/results/publication/${props.publication._id}`);
        
        if (Array.isArray(fallbackResponse.data)) {
          results.value = fallbackResponse.data;
          totalCount.value = fallbackResponse.data.length;
          totalPages.value = 1;
          currentPage.value = 1;
          await fetchStudentNames();
          console.log("✅ Fallback résultats réussi avec", fallbackResponse.data.length, "résultats");
        }
      } catch (fallbackError) {
        console.error("❌ Fallback résultats échoué:", fallbackError);
        if (!append) {
          results.value = [];
        }
      }
    } else if (!append) {
      results.value = [];
    }
  } finally {
    loading.value = false;
  }
};

// ✅ Fonction pour récupérer les noms des étudiants
const fetchStudentNames = async () => {
  try {
    const studentIds = [...new Set(results.value.map(r => {
      return typeof r.studentId === 'object' && r.studentId._id ? r.studentId._id : r.studentId;
    }))];
    if (studentIds.length === 0) return;
    const response = await api.post(`/students/getNames`, { studentIds });
    studentNames.value = response.data;
  } catch (error) {
    console.error("❌ Erreur lors de la récupération des noms des étudiants :", error);
  }
};

// ✅ Fonction pour récupérer les sessions des étudiants
const fetchStudentSessions = async () => {
  if (!props.publication._id) return [];
  try {
    const response = await api.get(`/publications/${props.publication._id}/student-sessions`
    );
    return response.data;
  } catch (error) {
    console.error("❌ Error fetching student sessions:", error);
    return [];
  }
};

// ✅ Fonction pour logguer les sessions des étudiants
const fetchAndLogStudentSessions = async () => {
  sessionData.value = await fetchStudentSessions();
  studentAttemptCount.value = sessionData.value.reduce((acc, session) => {
    // Corriger ici : utiliser studentID qui vient de l'API des sessions
    const studentIdStr = session.studentID; // L'API renvoie le studentID directement ici

    if (studentIdStr) { // Assure-toi que l'ID est valide
      acc[studentIdStr] = (acc[studentIdStr] || 0) + 1;
    }
    return acc;
  }, {});
  console.log("Tentatives par étudiant (keys):", Object.keys(studentAttemptCount.value));
  console.log("Tentatives par étudiant (values):", studentAttemptCount.value);
};


// ✅ Nouvelles méthodes pour la pagination
const loadNextPage = async () => {
  if (currentPage.value < totalPages.value && !loading.value) {
    await fetchResults(currentPage.value + 1, true);
  }
};

const goToPage = async (page) => {
  if (page !== currentPage.value && !loading.value) {
    await fetchResults(page, false);
  }
};

const handleSearch = async () => {
  currentPage.value = 1;
  await fetchResults(1, false);
};

const handleSort = async (newSortBy) => {
  if (sortBy.value === newSortBy) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortBy.value = newSortBy;
    sortOrder.value = 'desc';
  }
  currentPage.value = 1;
  await fetchResults(1, false);
};

const toggleView = async () => {
  showMetricsOnly.value = !showMetricsOnly.value;
  if (!showMetricsOnly.value && results.value.length === 0) {
    await fetchResults(1, false);
  }
};

// ✅ Computed properties optimisées qui utilisent les métriques du backend ou les données paginées
const totalStudents = computed(() => {
  return metricsData.value?.totalStudents || Object.keys(studentAttemptCount.value).length;
});

const totalAttempts = computed(() => {
  return metricsData.value?.totalAttempts || Object.values(studentAttemptCount.value).reduce((sum, count) => sum + count, 0);
});

const averageScore = computed(() => {
  if (metricsData.value?.averageScore !== undefined) {
    return Math.round(metricsData.value.averageScore);
  }
  
  if (results.value.length === 0) return 0;
  
  const scores = results.value.map(result => {
    const totalQuestions = result.responses.length;
    if (totalQuestions === 0) return 0;
    
    const correctAnswers = result.responses.reduce((sum, response) => {
      return sum + (parseFloat(response.score) || 0);
    }, 0);
    
    return (correctAnswers / totalQuestions) * 100;
  });
  
  const average = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  return Math.round(average);
});

const completionRate = computed(() => {
  if (metricsData.value?.completionRate !== undefined) {
    return Math.round(metricsData.value.completionRate);
  }
  
  const completedTests = results.value.filter(result => 
    result.responses && result.responses.length > 0
  ).length;
  
  return results.value.length > 0 ? Math.round((completedTests / results.value.length) * 100) : 0;
});

const scoreRanges = computed(() => {
  // Si on a les métriques du backend, on les utilise
  if (metricsData.value?.scoreDistribution) {
    const ranges = [
      { label: "0-20%", range: "0-20", color: "#dc3545" },
      { label: "21-40%", range: "21-40", color: "#fd7e14" },
      { label: "41-60%", range: "41-60", color: "#ffc107" },
      { label: "61-80%", range: "61-80", color: "#20c997" },
      { label: "81-100%", range: "81-100", color: "#198754" }
    ];
    
    const distribution = metricsData.value.scoreDistribution;
    const total = distribution.length;
    
    // Debug temporaire
    console.log("🔍 Score distribution (backend):", distribution);
    console.log("🔍 Total distribution:", total);
    
    return ranges.map(range => {
      const count = distribution.filter(d => d === range.range).length;
      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
      
      console.log(`🔍 Range ${range.label}: count=${count}, percentage=${percentage}%`);
      
      return {
        ...range,
        count,
        percentage
      };
    });
  }
  
  // Sinon, calcul basé sur les données paginées locales
  const ranges = [
    { label: "0-20%", min: 0, max: 20, count: 0, color: "#dc3545" },
    { label: "21-40%", min: 20.01, max: 40, count: 0, color: "#fd7e14" },
    { label: "41-60%", min: 40.01, max: 60, count: 0, color: "#ffc107" },
    { label: "61-80%", min: 60.01, max: 80, count: 0, color: "#20c997" },
    { label: "81-100%", min: 80.01, max: 100, count: 0, color: "#198754" }
  ];
  
  const calculatedScores = [];
  
  results.value.forEach(result => {
    const totalQuestions = result.responses.length;
    if (totalQuestions === 0) return;
    
    const correctAnswers = result.responses.reduce((sum, response) => {
      return sum + (parseFloat(response.score) || 0);
    }, 0);
    
    const percentage = (correctAnswers / totalQuestions) * 100;
    calculatedScores.push(percentage);
    
    const range = ranges.find(r => percentage >= r.min && percentage <= r.max);
    if (range) range.count++;
  });
  
  // Debug temporaire
  if (calculatedScores.length > 0) {
    console.log("🔍 Scores calculés (frontend):", calculatedScores);
    console.log("🔍 Ranges après calcul:", ranges.map(r => ({ label: r.label, count: r.count })));
  }
  
  const total = results.value.length;
  
  return ranges.map(range => ({
    ...range,
    percentage: total > 0 ? Math.round((range.count / total) * 100) : 0
  }));
});

const studentSummary = computed(() => {
  const summary = {};
  
  results.value.forEach(result => {
    const studentId = typeof result.studentId === 'object' && result.studentId?._id 
      ? result.studentId._id 
      : result.studentId;
    
    if (!summary[studentId]) {
      summary[studentId] = {
        studentId,
        name: studentNames.value[studentId] || "Unknown student",
        scores: [],
        attempts: [],
        lastAttempt: null
      };
    }
    
    // Calculer le score de cette tentative
    const totalQuestions = result.responses.length;
    const correctAnswers = result.responses.reduce((sum, response) => {
      return sum + (parseFloat(response.score) || 0);
    }, 0);
    const score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
    
    summary[studentId].scores.push(score);
    summary[studentId].attempts.push(result.submittedAt);
    
    // Mettre à jour la dernière tentative
    const attemptDate = new Date(result.submittedAt);
    if (!summary[studentId].lastAttempt || attemptDate > new Date(summary[studentId].lastAttempt)) {
      summary[studentId].lastAttempt = result.submittedAt;
    }
  });
  
  return Object.values(summary).map(student => ({
    ...student,
    attemptCount: student.scores.length,
    bestScore: Math.max(...student.scores),
    averageScore: Math.round(student.scores.reduce((sum, score) => sum + score, 0) / student.scores.length)
  })).sort((a, b) => b.bestScore - a.bestScore);
});

// ✅ Méthodes pour le formatage et les classes CSS
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getScoreClass = (score) => {
  if (score >= 80) return 'score-excellent';
  if (score >= 60) return 'score-good';
  if (score >= 40) return 'score-average';
  return 'score-poor';
};

const getStatusClass = (score) => {
  if (score >= 80) return 'bg-success';
  if (score >= 60) return 'bg-info';
  if (score >= 40) return 'bg-warning';
  return 'bg-danger';
};

const getStatusLabel = (score) => {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Average';
  return 'Poor';
};

// ✅ Pages visibles pour la pagination
const visiblePages = computed(() => {
  const delta = 2;
  const range = [];
  const rangeWithDots = [];
  
  for (let i = Math.max(2, currentPage.value - delta); 
       i <= Math.min(totalPages.value - 1, currentPage.value + delta); 
       i++) {
    range.push(i);
  }
  
  if (currentPage.value - delta > 2) {
    rangeWithDots.push(1, '...');
  } else {
    rangeWithDots.push(1);
  }
  
  rangeWithDots.push(...range);
  
  if (currentPage.value + delta < totalPages.value - 1) {
    rangeWithDots.push('...', totalPages.value);
  } else if (totalPages.value > 1) {
    rangeWithDots.push(totalPages.value);
  }
  
  return rangeWithDots.filter((item, index, arr) => arr.indexOf(item) === index);
});






// ✅ Surveiller les changements de publication et recharger les données
watch(
  () => props.publication,
  async () => {
    console.log("🔄 Mise à jour de PublicationResult avec une nouvelle publication !");
    
    // Réinitialiser l'état
    currentPage.value = 1;
    results.value = [];
    searchQuery.value = '';
    
    // Charger les métriques en priorité (rapide)
    await fetchMetrics();
    
    // Charger les sessions pour les tentatives
    await fetchAndLogStudentSessions();
    
    // Charger les détails du test
    await fetchTest();
    
    // Si on est en mode détail, charger la première page
    if (!showMetricsOnly.value) {
      await fetchResults(1, false);
    }
  },
  { deep: true, immediate: true }
);

// ✅ Charger les données au montage
onMounted(async () => {
  // Chargement optimisé : métriques d'abord
  await fetchMetrics();
  await fetchAndLogStudentSessions();
  await fetchTest();
  
  // Charger les détails seulement si nécessaire
  if (!showMetricsOnly.value) {
    await fetchResults(1, false);
  }
});
</script>


<style scoped>
.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
}

.no-results {
  font-size: 16px;
  color: #777;
  text-align: center;
  margin-top: 20px;
}

.badge {
  background-color: #007bff;
  color: white;
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 12px;
  margin-left: 10px;
}

/* Dashboard synthétique */
.results-dashboard {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.metric-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  height: 100%;
}

.metric-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 5px;
}

.metric-label {
  color: #6c757d;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.chart-card, .table-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.score-distribution {
  margin-top: 15px;
}

.score-bar-container {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  gap: 15px;
}

.score-label {
  font-weight: 500;
  min-width: 80px;
  font-size: 0.9rem;
}

.score-bar {
  flex: 1;
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
}

.score-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.score-count {
  min-width: 80px;
  text-align: right;
  font-size: 0.85rem;
  color: #6c757d;
}

/* Badges de score */
.score-badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: bold;
  font-size: 0.85rem;
}

.score-excellent {
  background: #d4edda;
  color: #155724;
}

.score-good {
  background: #d1ecf1;
  color: #0c5460;
}

.score-average {
  background: #fff3cd;
  color: #856404;
}

.score-poor {
  background: #f8d7da;
  color: #721c24;
}

/* Table améliorée */
.table-hover tbody tr:hover {
  background-color: rgba(0,0,0,0.025);
}

.table-sm th, .table-sm td {
  padding: 8px 12px;
}

/* Responsive design */
@media (max-width: 768px) {
  .metric-card {
    margin-bottom: 15px;
  }
  
  .metric-number {
    font-size: 2rem;
  }
  
  .score-bar-container {
    flex-direction: column;
    gap: 5px;
    align-items: stretch;
  }
  
  .score-label, .score-count {
    text-align: center;
  }
}

/* Animations */
.metric-card, .chart-card, .table-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.metric-card:hover, .chart-card:hover, .table-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
}

/* Contrôles de pagination et recherche */
.controls-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.pagination .page-link {
  border-color: #dee2e6;
  color: #6c757d;
}

.pagination .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
}

.pagination .page-item.disabled .page-link {
  color: #adb5bd;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}

.btn-group .btn.active {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

/* Amélioration des spinners */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* États de chargement */
.table tbody tr.loading-row {
  background-color: #f8f9fa;
}

/* Améliorations responsive */
@media (max-width: 768px) {
  .controls-section .col-md-4 {
    margin-bottom: 10px;
  }
  
  .pagination {
    justify-content: center;
  }
  
  .pagination .page-item {
    margin: 0 2px;
  }
  
  .btn-group {
    width: 100%;
  }
  
  .btn-group .btn {
    flex: 1;
  }
}
</style>
